import React, { useMemo, useState, useEffect } from "react";
import { Globe, MapPin, MagnifyingGlass, Check, X } from "@phosphor-icons/react";

/**
 * LocationPicker — asks the seeker which timezone they're currently in.
 *
 * Two modes:
 *  - "confirm": shows the browser-detected tz with a "Yes, that's me" / "Change" pair.
 *  - "search":  a searchable list of every IANA timezone supported by the browser.
 *
 * Persists the choice in localStorage so we don't ask repeatedly. Exposes a small
 * "edit" affordance so a travelling user can update their location anytime.
 */

const STORAGE_KEY = "akb_current_tz_v1";

export function getStoredTz() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredTz(tz) {
  try {
    if (tz) localStorage.setItem(STORAGE_KEY, tz);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function detectBrowserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
}

function listAllTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch {
    /* fall through */
  }
  // Fallback list (curated, ~60 popular zones) for browsers without supportedValuesOf
  return [
    "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi",
    "America/Anchorage", "America/Argentina/Buenos_Aires", "America/Bogota",
    "America/Caracas", "America/Chicago", "America/Denver", "America/Halifax",
    "America/Los_Angeles", "America/Mexico_City", "America/New_York",
    "America/Phoenix", "America/Santiago", "America/Sao_Paulo", "America/Toronto",
    "America/Vancouver", "Asia/Bangkok", "Asia/Beirut", "Asia/Colombo",
    "Asia/Dhaka", "Asia/Dubai", "Asia/Hong_Kong", "Asia/Jakarta",
    "Asia/Jerusalem", "Asia/Karachi", "Asia/Kathmandu", "Asia/Kolkata",
    "Asia/Kuala_Lumpur", "Asia/Manila", "Asia/Riyadh", "Asia/Seoul",
    "Asia/Shanghai", "Asia/Singapore", "Asia/Tashkent", "Asia/Tehran",
    "Asia/Tokyo", "Atlantic/Reykjavik", "Australia/Adelaide", "Australia/Brisbane",
    "Australia/Melbourne", "Australia/Perth", "Australia/Sydney", "Europe/Amsterdam",
    "Europe/Athens", "Europe/Berlin", "Europe/Brussels", "Europe/Bucharest",
    "Europe/Dublin", "Europe/Helsinki", "Europe/Istanbul", "Europe/Lisbon",
    "Europe/London", "Europe/Madrid", "Europe/Moscow", "Europe/Paris",
    "Europe/Prague", "Europe/Rome", "Europe/Stockholm", "Europe/Vienna",
    "Europe/Warsaw", "Europe/Zurich", "Pacific/Auckland", "Pacific/Honolulu",
    "UTC",
  ];
}

function offsetFor(tz) {
  try {
    const d = new Date();
    const dtf = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      timeZoneName: "shortOffset",
      hour: "2-digit",
    });
    const parts = dtf.formatToParts(d);
    const off = parts.find((p) => p.type === "timeZoneName")?.value || "";
    return off.replace("GMT", "UTC") || "UTC";
  } catch {
    return "UTC";
  }
}

function prettyCity(tz) {
  // "Asia/Kolkata" → "Kolkata, Asia"
  // "America/Argentina/Buenos_Aires" → "Buenos Aires, Argentina"
  const parts = tz.split("/");
  const city = (parts.pop() || tz).replace(/_/g, " ");
  const region = parts[parts.length - 1]?.replace(/_/g, " ") || "";
  return region ? `${city}, ${region}` : city;
}

function localDateLabel(tz) {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: tz,
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date());
  } catch {
    return "";
  }
}

export default function LocationPicker({ initialTz, onConfirm, onCancel }) {
  const detected = useMemo(() => detectBrowserTz(), []);
  const startTz = initialTz || detected;
  const [mode, setMode] = useState("confirm");
  const [selectedTz, setSelectedTz] = useState(startTz);
  const [query, setQuery] = useState("");

  const allZones = useMemo(() => listAllTimezones(), []);
  const filtered = useMemo(() => {
    if (!query.trim()) return allZones.slice(0, 80);
    const q = query.trim().toLowerCase();
    return allZones
      .filter((tz) => tz.toLowerCase().includes(q) || prettyCity(tz).toLowerCase().includes(q))
      .slice(0, 120);
  }, [allZones, query]);

  const confirm = (tz) => {
    setStoredTz(tz);
    onConfirm?.(tz);
  };

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] to-[#1A1410] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40"
      data-testid="horoscope-location-picker"
    >
      <div className="flex items-center gap-2 text-amber-300 text-[11px] uppercase tracking-widest mb-3">
        <Globe size={14} weight="fill" /> Where in the world are you right now?
      </div>

      {mode === "confirm" ? (
        <>
          <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight">
            We need your current location to tune today's reading.
          </h2>
          <p className="mt-3 text-amber-50/80 text-sm sm:text-base leading-relaxed max-w-xl">
            Your horoscope's "today", the Moon-transit, Chandrashtama window, and the best-time
            recommendations all shift with where you actually are right now — not where you were born.
          </p>

          <div className="mt-6 rounded-xl bg-amber-50/5 border border-amber-700/30 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <MapPin size={22} weight="duotone" className="text-amber-300 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-amber-300/80">
                  Detected from your device
                </div>
                <div
                  className="font-serif-display text-xl sm:text-2xl text-amber-50 mt-1 leading-tight break-words"
                  data-testid="horoscope-location-detected-city"
                >
                  {prettyCity(selectedTz)}
                </div>
                <div className="text-amber-50/70 text-sm mt-1">
                  {selectedTz} · {offsetFor(selectedTz)}
                </div>
                <div className="text-amber-50/70 text-sm mt-2">
                  Local date here: <span className="text-amber-100">{localDateLabel(selectedTz)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => confirm(selectedTz)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-[#1A1410] font-semibold text-sm tracking-wide transition"
              data-testid="horoscope-location-confirm-btn"
            >
              <Check size={16} weight="bold" /> Yes, that's me — show today's reading
            </button>
            <button
              type="button"
              onClick={() => setMode("search")}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-transparent border border-amber-300/60 text-amber-100 hover:bg-amber-300/10 font-medium text-sm transition"
              data-testid="horoscope-location-change-btn"
            >
              I'm somewhere else — change location
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-amber-100/70 hover:text-amber-100 text-sm transition"
                data-testid="horoscope-location-cancel-btn"
              >
                <X size={14} /> Cancel
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight">
            Where are you right now?
          </h2>
          <p className="mt-2 text-amber-50/80 text-sm">
            Search by city or country.
          </p>

          <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50/5 border border-amber-700/40 px-3 py-2">
            <MagnifyingGlass size={16} className="text-amber-200/70 flex-shrink-0" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Try 'Tokyo', 'New York', 'Dubai'..."
              className="bg-transparent text-amber-50 placeholder:text-amber-100/40 text-sm flex-1 outline-none"
              data-testid="horoscope-location-search-input"
            />
          </div>

          <div className="mt-3 max-h-[260px] overflow-y-auto rounded-xl bg-amber-50/5 border border-amber-700/30 divide-y divide-amber-700/20">
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center text-amber-100/60 text-sm">
                No match — try a different spelling.
              </div>
            ) : (
              filtered.map((tz) => (
                <button
                  key={tz}
                  type="button"
                  onClick={() => {
                    setSelectedTz(tz);
                    confirm(tz);
                  }}
                  className={`w-full text-left px-4 py-2.5 hover:bg-amber-300/10 transition flex items-center justify-between gap-3 ${
                    tz === selectedTz ? "bg-amber-300/10" : ""
                  }`}
                  data-testid={`horoscope-location-option-${tz.replace(/[\/_]/g, "-").toLowerCase()}`}
                >
                  <div className="min-w-0">
                    <div className="text-amber-50 text-sm truncate">{prettyCity(tz)}</div>
                    <div className="text-amber-100/50 text-[11px] truncate">{tz}</div>
                  </div>
                  <div className="text-amber-200/80 text-xs flex-shrink-0">{offsetFor(tz)}</div>
                </button>
              ))
            )}
          </div>

          <div className="mt-4 flex justify-between items-center">
            <button
              type="button"
              onClick={() => setMode("confirm")}
              className="text-amber-100/80 hover:text-amber-100 text-sm"
              data-testid="horoscope-location-back-btn"
            >
              ← Back
            </button>
            <div className="text-amber-100/50 text-xs">
              {filtered.length} location{filtered.length === 1 ? "" : "s"}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { prettyCity, offsetFor, localDateLabel, detectBrowserTz };
